import { Button, Classes, Tag, Tooltip } from "@blueprintjs/core";
import { ICellRendererParams } from "ag-grid-community";
import classNames from "classnames";
import {
    getClusterProjectDisplayName,
    getStudyGroupDisplayLabel
} from "in_queue/types/clusterProjectRowType";
import { Cluster } from "in_queue/types/clusterType";
import {
    getProjectDisplayName,
    isPriorQueueProject,
    isWithdrawnByDefault
} from "in_queue/types/projectType";
import { AgGridColumnDefs } from "../tables/agGrid/agGridHelpers";
import {
    baseScenarioColumnGroup,
    BaseScenarioColumnId
} from "../tables/agGrid/baseScenarioColumns";
import {
    formatConstraintStudyCycle,
    QueueCellRenderer
} from "../tables/agGrid/cellRenderers";
import { FuelTypePillRenderer } from "../tables/FuelTypePill";
import {
    ProjectPickerRow,
    reducedSize,
    reductionForSize
} from "./projectPickerRow";
import css from "./ProjectPickerTable.module.scss";
import { ReductionField } from "./ReductionField";
import { ProjectAssumptionsEditor } from "./useProjectAssumptionsEditor";

export type ColumnId =
    | "select"
    | "project"
    | "queue"
    | "subregion"
    | "fuelType"
    | "erisSize"
    | "nrisSize"
    | "erisReduction"
    | "nrisReduction"
    | "action"
    | BaseScenarioColumnId;

const BASE_SCENARIO_TOOLTIP =
    "From the cluster's latest base scenario. Projects it did not rank show no value.";

const PQ_ERIS_TOOLTIP =
    "Prior-queue (PQ) ERIS can only be 0% or 100%, since PQ ERIS dispatch is modified for CQ fuel-based dispatch.";

const WHOLE_PROJECT_ONLY_TOOLTIP =
    "This project is studied in service or dropped, never derated, so no percentage applies. Use Drop to take it out.";

const QUEUE_TOOLTIP =
    "The study cycle the project is in. Projects from an earlier cycle than this one are tagged PQ.";

export interface PickerContext {
    readonly editor: ProjectAssumptionsEditor;
    readonly capacityOnlyProjectIds: ReadonlySet<string>;
    readonly wholeProjectOnlyProjectIds: ReadonlySet<string>;
}

const projectLabel = (row: ProjectPickerRow) =>
    row.stats
        ? getClusterProjectDisplayName(row.stats)
        : getProjectDisplayName(row.project);

const projectCellRenderer = (params: ICellRendererParams<ProjectPickerRow>) => {
    const row = params.data;
    if (!row) {
        return null;
    }
    const { project } = row;

    return (
        <div className={css["ProjectPickerTable-project-cell"]}>
            <span
                className={classNames(
                    css["ProjectPickerTable-project-name"],
                    Classes.TEXT_LARGE
                )}
            >
                {projectLabel(row)}
            </span>
            {isWithdrawnByDefault(project) && (
                <Tag
                    minimal
                    round
                    intent="danger"
                    className={css["ProjectPickerTable-project-tag"]}
                >
                    Withdrawn
                </Tag>
            )}
            {row.isCurrentProject ? (
                <Tag
                    minimal
                    round
                    intent="primary"
                    className={css["ProjectPickerTable-project-tag"]}
                >
                    Current
                </Tag>
            ) : row.isTracked ? (
                <Tag
                    minimal
                    round
                    intent="success"
                    className={css["ProjectPickerTable-project-tag"]}
                >
                    Portfolio
                </Tag>
            ) : null}
        </div>
    );
};

const queueCellRenderer =
    (cluster: Cluster) => (params: ICellRendererParams<ProjectPickerRow>) => {
        const row = params.data;
        return row ? (
            <QueueCellRenderer
                cycle={formatConstraintStudyCycle(
                    cluster.region,
                    row.project.cluster.studyCycle
                )}
                priorQueue={isPriorQueueProject(row.project.cluster, cluster)}
            />
        ) : null;
    };

// Matches the two decimals the MW cells accept.
const formatMw = (mw: number): string => String(Math.round(mw * 100) / 100);

const sizeCellRenderer =
    (sizeKey: "erisMw" | "nrisMw", side: "eris" | "nris", editable: boolean) =>
    (params: ICellRendererParams<ProjectPickerRow>) => {
        const row = params.data;
        const context: PickerContext | undefined = params.context;
        if (!row) {
            return null;
        }
        const { project } = row;
        const full = project.size[sizeKey];
        const reduced = reducedSize(row)[sizeKey];
        if (editable && context !== undefined) {
            return (
                <span className={css["ProjectPickerTable-size-cell"]}>
                    {full !== reduced && (
                        <span
                            className={css["ProjectPickerTable-size-original"]}
                        >
                            {formatMw(full)}
                        </span>
                    )}
                    <ReductionField
                        value={reduced}
                        max={full}
                        unit=""
                        decimals={2}
                        isSet={reduced < full}
                        commitEmpty={false}
                        disabled={
                            full === 0 ||
                            row.isExcluded ||
                            context.wholeProjectOnlyProjectIds.has(
                                project.projectId
                            ) ||
                            (side === "eris" &&
                                context.capacityOnlyProjectIds.has(
                                    project.projectId
                                ))
                        }
                        ariaLabel={`${side.toUpperCase()} size, MW`}
                        onChange={(next) =>
                            context.editor.setReduction({
                                projectId: project.projectId,
                                projectType: project.type,
                                [side]: reductionForSize(full, next)
                            })
                        }
                    />
                </span>
            );
        }
        return full === reduced ? (
            <span>{formatMw(full)}</span>
        ) : (
            <span className={css["ProjectPickerTable-size-cell"]}>
                <span className={css["ProjectPickerTable-size-original"]}>
                    {formatMw(full)}
                </span>
                <span className={css["ProjectPickerTable-size-reduced"]}>
                    {formatMw(reduced)}
                </span>
            </span>
        );
    };

const reductionCellRenderer =
    (side: "eris" | "nris") =>
    (params: ICellRendererParams<ProjectPickerRow>) => {
        const row = params.data;
        const context: PickerContext | undefined = params.context;
        if (!row || !context) {
            return null;
        }
        if (row.isExcluded) {
            return <span className={Classes.TEXT_MUTED}>100 %</span>;
        }
        const { project } = row;
        const eris = row.reduction?.eris ?? 0;
        const nris = row.reduction?.nris ?? 0;
        const withdrawOnly =
            side === "eris" &&
            context.capacityOnlyProjectIds.has(project.projectId);

        if (context.wholeProjectOnlyProjectIds.has(project.projectId)) {
            return (
                <Tooltip compact content={WHOLE_PROJECT_ONLY_TOOLTIP}>
                    <span className={Classes.TEXT_MUTED}>n/a</span>
                </Tooltip>
            );
        }

        const value = side === "eris" ? eris : nris;
        const field = (
            <ReductionField
                value={value}
                ariaLabel={`${side.toUpperCase()} reduction, percent`}
                onChange={(next) =>
                    context.editor.setReduction({
                        projectId: project.projectId,
                        projectType: project.type,
                        [side]: next
                    })
                }
                {...(withdrawOnly ? { stepSize: 100, readOnly: true } : {})}
            />
        );
        return withdrawOnly ? (
            <Tooltip compact content={PQ_ERIS_TOOLTIP}>
                {field}
            </Tooltip>
        ) : (
            field
        );
    };

const actionCellRenderer = (params: ICellRendererParams<ProjectPickerRow>) => {
    const row = params.data;
    const context: PickerContext | undefined = params.context;
    if (!row || !context) {
        return null;
    }
    return (
        <Button
            icon={row.isExcluded ? "add" : "remove"}
            aria-label={row.isExcluded ? "Include project" : "Exclude project"}
            variant="minimal"
            size="small"
            onClick={() =>
                context.editor.setIncluded({
                    projectId: row.project.projectId,
                    projectType: row.project.type,
                    included: row.isExcluded
                })
            }
        />
    );
};

export const getColumnDefs = ({
    energyLabel,
    capacityLabel,
    showReduction,
    cluster,
    showSubregion,
    showQueue
}: {
    readonly energyLabel: string;
    readonly capacityLabel: string;
    readonly showReduction: boolean;
    readonly cluster: Cluster;
    readonly showSubregion: boolean;
    readonly showQueue: boolean;
}): AgGridColumnDefs<ProjectPickerRow, ColumnId> => [
    {
        colId: "project",
        headerName: "Project",
        flex: 1.5,
        minWidth: 240,
        valueGetter: ({ data }) => (data ? projectLabel(data) : null),
        cellRenderer: projectCellRenderer
    },
    ...(showQueue
        ? [
              {
                  colId: "queue" as const,
                  headerName: "Queue",
                  headerTooltip: QUEUE_TOOLTIP,
                  flex: 0.8,
                  minWidth: 124,
                  valueGetter: ({
                      data
                  }: {
                      data: ProjectPickerRow | undefined;
                  }) => {
                      if (!data) return null;
                      const label = formatConstraintStudyCycle(
                          cluster.region,
                          data.project.cluster.studyCycle
                      );
                      return isPriorQueueProject(data.project.cluster, cluster)
                          ? `${label} PQ`
                          : label;
                  },
                  cellRenderer: queueCellRenderer(cluster)
              }
          ]
        : []),
    ...(showSubregion
        ? [
              {
                  colId: "subregion" as const,
                  headerName: "Subregion",
                  headerTooltip:
                      "The project's study group within its cluster.",
                  flex: 0.8,
                  minWidth: 118,
                  valueGetter: ({
                      data
                  }: {
                      data: ProjectPickerRow | undefined;
                  }) =>
                      getStudyGroupDisplayLabel(
                          cluster.region,
                          data?.project.cluster.studyGroup
                      )
              }
          ]
        : []),
    {
        colId: "fuelType",
        headerName: "Fuel Type",
        flex: 1,
        minWidth: 120,
        valueGetter: ({ data }) => data?.project.fuelType ?? null,
        cellRenderer: FuelTypePillRenderer
    },
    ...(
        [
            [energyLabel, "erisSize", "erisMw", "erisReduction", "eris"],
            [capacityLabel, "nrisSize", "nrisMw", "nrisReduction", "nris"]
        ] as const
    ).map(([label, sizeColId, sizeKey, reductionColId, side]) => ({
        headerName: label,
        children: [
            {
                colId: sizeColId,
                headerName: "MW",
                type: "numericColumn",
                flex: 0.8,
                minWidth: showReduction ? 150 : 110,
                // allow left/right arrow keys to move between digits
                suppressKeyboardEvent: ({ event }: { event: KeyboardEvent }) =>
                    event.target instanceof HTMLInputElement,
                valueGetter: ({
                    data
                }: {
                    data: ProjectPickerRow | undefined;
                }) => (data ? reducedSize(data)[sizeKey] : null),
                cellRenderer: sizeCellRenderer(sizeKey, side, showReduction)
            },
            ...(showReduction
                ? [
                      {
                          colId: reductionColId,
                          headerName: "Reduction",
                          flex: 0.5,
                          minWidth: 118,
                          // allow left/right arrow keys to move between digits
                          suppressKeyboardEvent: ({
                              event
                          }: {
                              event: KeyboardEvent;
                          }) => event.target instanceof HTMLInputElement,
                          valueGetter: ({
                              data
                          }: {
                              data: ProjectPickerRow | undefined;
                          }) => data?.reduction?.[side] ?? 0,
                          cellRenderer: reductionCellRenderer(side)
                      }
                  ]
                : [])
        ]
    })),
    baseScenarioColumnGroup<ProjectPickerRow>({
        getStats: (data) => data?.stats,
        headerTooltip: BASE_SCENARIO_TOOLTIP
    }),
    {
        colId: "action",
        headerName: "",
        pinned: "right",
        suppressMovable: true,
        width: 80,
        sortable: false,
        resizable: false,
        cellRenderer: actionCellRenderer
    }
];
